import axios from "axios";
import { State } from "../state";
import {
  setData,
  setTasks,
  setProjectPlan,
  setInvoices,
  setAttachments,
  setTriage,
} from "./actions";
import { BASE_URL } from "../../app/appConstants";
import { handleRequestError, renewAccessToken } from "../common";
import { Task, FileUploadResult } from "../../types";
import { AppDispatch } from "../configureStore";

export const requestData =
  (customerId: number) => (dispatch: AppDispatch, getState: () => State) => {
    const accessToken = getState().authentication.accessToken;
    if (!accessToken) {
      return Promise.reject();
    }
    return axios
      .get(`${BASE_URL}/mps/web/claim/all`, {
        headers: {
          Authorization: "Bearer " + accessToken,
          accept: "application/json",
        },
        params: {
          customerId: customerId,
        },
      })
      .then((response) => {
        dispatch(setData(response.data));
        return Promise.resolve();
      })
      .catch((error) => {
        return dispatch(
          handleRequestError(() => requestData(customerId), error)
        );
      });
  };

export const requestGetTasksByClaimId =
  (id: number, customerId: number) =>
  (dispatch: AppDispatch, getState: () => State) => {
    const accessToken = getState().authentication.accessToken;
    if (!accessToken) {
      return Promise.reject();
    }

    return axios
      .get(`${BASE_URL}/mps/web/claim/${id}/tasks`, {
        headers: {
          Authorization: "Bearer " + accessToken,
          accept: "application/json",
        },
        params: {
          customerId: customerId,
        },
      })
      .then((response) => {
        dispatch(setTasks(response.data));
        return Promise.resolve();
      })
      .catch((error) => {
        return dispatch(
          handleRequestError(
            () => requestGetTasksByClaimId(id, customerId),
            error
          )
        );
      });
  };

export const requestGetProjectPlanByClaimId =
  (id: number) => (dispatch: AppDispatch, getState: () => State) => {
    const accessToken = getState().authentication.accessToken;
    if (!accessToken) {
      return Promise.reject();
    }

    return axios
      .get(`${BASE_URL}/mps/web/claim/${id}/projectplan`, {
        headers: {
          Authorization: "Bearer " + accessToken,
          accept: "application/json",
        },
      })
      .then((response) => {
        response.data.sort((task1: Task, task2: Task) => {
          const aInt = parseInt(task1.orderNumber);
          const bInt = parseInt(task2.orderNumber);
          if (aInt < bInt) {
            return -1;
          } else if (aInt > bInt) {
            return 1;
          } else {
            return 0;
          }
        });
        dispatch(setProjectPlan(response.data));
        return Promise.resolve();
      })
      .catch((error) => {
        return dispatch(
          handleRequestError(() => requestGetProjectPlanByClaimId(id), error)
        );
      });
  };

export const requestGetInvoicesByClaimId =
  (id: number) => (dispatch: AppDispatch, getState: () => State) => {
    const accessToken = getState().authentication.accessToken;
    if (!accessToken) {
      return Promise.reject();
    }

    return axios
      .get(`${BASE_URL}/mps/web/claim/${id}/invoices`, {
        headers: {
          Authorization: "Bearer " + accessToken,
          accept: "application/json",
        },
      })
      .then((response) => {
        dispatch(setInvoices(response.data));
        return Promise.resolve();
      })
      .catch((error) => {
        return dispatch(
          handleRequestError(() => requestGetInvoicesByClaimId(id), error)
        );
      });
  };

export const openTriage =
  (claimId: number) =>
  (dispatch: AppDispatch, getState: () => State) => {
    return new Promise<void>((resolve, reject) => {
      dispatch(renewAccessToken(() => requestGetInvoicesByClaimId(claimId)));
      resolve();
    })
      .then(() => {
        setTimeout(() => {
          const accessToken = getState().authentication.accessToken;
          const refreshToken = getState().authentication.refreshToken;
          const url = `${BASE_URL}/mps/web/pf/internal/triage?doc=${claimId}&access_token=${accessToken}&refresh_token=${refreshToken}`;
          window.open(url, "_blank");
        }, 1000);
        return Promise.resolve();
      })
      .catch((e) => {
        console.log(e);
      });
  };

export const requestGetAttachmentsByClaimId =
  (id: number) => (dispatch: AppDispatch, getState: () => State) => {
    const accessToken = getState().authentication.accessToken;
    if (!accessToken) {
      return Promise.reject();
    }

    return axios
      .get(`${BASE_URL}/mps/web/claim/${id}/attachments`, {
        headers: {
          Authorization: "Bearer " + accessToken,
          accept: "application/json",
        },
      })
      .then((response) => {
        dispatch(setAttachments(response.data));
        return Promise.resolve();
      })
      .catch((error) => {
        return dispatch(
          handleRequestError(() => requestGetAttachmentsByClaimId(id), error)
        );
      });
  };

export const requestUploadAttachment =
  (id: number, file: File, name: string, description: string) =>
  (dispatch: AppDispatch, getState: () => State) => {
    const accessToken = getState().authentication.accessToken;
    if (!accessToken) {
      return Promise.reject();
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", name);
    formData.append("description", description);
    formData.append("claimId", String(id));

    return axios
      .post(`${BASE_URL}/files/upload`, formData, {
        headers: {
          Authorization: "Bearer " + accessToken,
          accept: "application/json",
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        const result: FileUploadResult = response.data;
        if (result.status === "UPLOADED" || result.status === "QUEUED") {
          dispatch(requestGetAttachmentsByClaimId(id));
        }
        return result;
      })
      .catch((error) => {
        return dispatch(
          handleRequestError(() => requestUploadAttachment(id, file, name, description), error)
        );
      });
  };

export const requestGetTriageDetailsByClaimId =
  (id: number) => (dispatch: AppDispatch, getState: () => State) => {
    const accessToken = getState().authentication.accessToken;
    if (!accessToken) {
      return Promise.reject();
    }

    return axios
      .get(`${BASE_URL}/mps/web/claim/${id}/triageDetails`, {
        headers: {
          Authorization: "Bearer " + accessToken,
          accept: "application/json",
        },
      })
      .then((response) => {
        dispatch(setTriage(response.data));
        return Promise.resolve();
      })
      .catch((error) => {
        return dispatch(
          handleRequestError(() => requestGetTriageDetailsByClaimId(id), error)
        );
      });
  };
